import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import elementSettings from 'src/js/modules/element-settings';
import { buildShadowValue } from 'src/js/modules/breakpoint-editing/helpers/build-shadow-value';
import useAutoPaddingSync from '../hooks/use-auto-padding-sync';
import controlIds from '../constants/control-ids';

const {
  Context: ElementSettingsContext,
  components: { Toggle },
  helpers: { createBatch, combineBatches, convertPlainBatchToBreakpointBasedBatch, getBatchValues },
} = elementSettings;

const ENABLED_VALUE = buildShadowValue('0px', '6px', '20px', '0px', 'rgba(0,0,0,0.18)');
const DISABLED_VALUE = 'none';

function ShadowControl({ elementIds, targets, onChange }) {
  const { resolvedState, deviceMode } = useContext(ElementSettingsContext);
  const { getAutoPaddingBatch } = useAutoPaddingSync();

  const isToggleOn = useMemo(() => {
    const values = getBatchValues(resolvedState, { elementIds, targets }).flat();

    return values.some((value) => value !== undefined && value !== DISABLED_VALUE);
  }, [resolvedState, elementIds, targets]);

  const handleChange = useCallback(
    (isActive) => {
      const value = isActive ? ENABLED_VALUE : DISABLED_VALUE;
      const breakpointBasedBatch = convertPlainBatchToBreakpointBasedBatch(
        createBatch({ elementIds, targets, value }),
        deviceMode.breakpointKey
      );

      onChange({
        batch: combineBatches([
          breakpointBasedBatch,
          getAutoPaddingBatch(breakpointBasedBatch, { elementIds }),
        ]),
        controlId: controlIds.SHADOW,
        value: isActive,
      });
    },
    [deviceMode.breakpointKey, elementIds, getAutoPaddingBatch, onChange, targets]
  );

  return (
    <Toggle isToggleEnabled={isToggleOn} title={i18next.t('Shadow')} handleChange={handleChange} />
  );
}

ShadowControl.propTypes = {
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default ShadowControl;
